import React, { useRef, useState } from 'react';
import { Trash2, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import InlineEnumeratedSlotValue from '../../../../types/BotBuilder/InlineEnumeratedSlotValue';

interface EnumeratedValuesManagerProps {
  enumeratedValues: InlineEnumeratedSlotValue[];
  onValuesChange: (values: InlineEnumeratedSlotValue[]) => void;
  disabled?: boolean;
}

function EnumeratedValuesManager({
  enumeratedValues,
  onValuesChange,
  disabled = false
}: Readonly<EnumeratedValuesManagerProps>): React.ReactElement {
  const stableKeysRef = useRef<string[]>([]);
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(
    new Set()
  );

  while (stableKeysRef.current.length < enumeratedValues.length) {
    stableKeysRef.current.push(`enum-${Date.now()}-${Math.random()}`);
  }
  if (stableKeysRef.current.length > enumeratedValues.length) {
    stableKeysRef.current = stableKeysRef.current.slice(
      0,
      enumeratedValues.length
    );
  }

  const handleValueChange = (index: number, value: string): void => {
    const newValues = [...enumeratedValues];
    newValues[index] = { ...newValues[index], value };
    onValuesChange(newValues);
  };

  const handleSynonymsChange = (index: number, synonyms: string[]): void => {
    const newValues = [...enumeratedValues];
    newValues[index] = { ...newValues[index], synonyms };
    onValuesChange(newValues);
  };

  const handleSynonymChange = (
    valueIndex: number,
    synonymIndex: number,
    synonym: string
  ): void => {
    const newSynonyms = [...enumeratedValues[valueIndex].synonyms];
    newSynonyms[synonymIndex] = synonym;
    handleSynonymsChange(valueIndex, newSynonyms);
  };

  const handleAddSynonym = (valueIndex: number): void => {
    const newSynonyms = [...enumeratedValues[valueIndex].synonyms, ''];
    handleSynonymsChange(valueIndex, newSynonyms);
  };

  const handleRemoveSynonym = (
    valueIndex: number,
    synonymIndex: number
  ): void => {
    const newSynonyms = enumeratedValues[valueIndex].synonyms.filter(
      (_, i) => i !== synonymIndex
    );
    handleSynonymsChange(valueIndex, newSynonyms);
  };

  const handleRemoveValue = (index: number): void => {
    const newValues = enumeratedValues.filter((_, i) => i !== index);
    stableKeysRef.current = stableKeysRef.current.filter((_, i) => i !== index);
    const newExpandedIndices = new Set<number>();
    expandedIndices.forEach((i) => {
      if (i < index) {
        newExpandedIndices.add(i);
      } else if (i > index) {
        newExpandedIndices.add(i - 1);
      }
    });
    setExpandedIndices(newExpandedIndices);
    onValuesChange(newValues);
  };

  const handleAddValue = (): void => {
    onValuesChange([...enumeratedValues, { value: '', synonyms: [] }]);
  };

  const toggleExpanded = (index: number): void => {
    const newExpandedIndices = new Set(expandedIndices);
    if (newExpandedIndices.has(index)) {
      newExpandedIndices.delete(index);
    } else {
      newExpandedIndices.add(index);
    }
    setExpandedIndices(newExpandedIndices);
  };

  return (
    <div className='space-y-2'>
      <div className='text-sm font-medium'>Enumerated values</div>
      {enumeratedValues.map((enumValue, index) => {
        const isExpanded = expandedIndices.has(index);
        return (
          <div key={stableKeysRef.current[index]} className='space-y-1'>
            <div className='flex gap-2 items-center'>
              <CustomInput
                value={enumValue.value}
                onChange={(e) => handleValueChange(index, e.target.value)}
                placeholder={`Value ${index + 1}`}
                className='bg-white flex-1'
                containerClassName='mb-0'
                disabled={disabled}
              />
              <button
                type='button'
                onClick={() => toggleExpanded(index)}
                className='text-gray-500 hover:text-gray-700 p-1'
                title={isExpanded ? 'Hide synonyms' : 'Show synonyms'}
                disabled={disabled}
              >
                {isExpanded ? (
                  <ChevronDown className='h-4 w-4' />
                ) : (
                  <ChevronRight className='h-4 w-4' />
                )}
              </button>
              {enumeratedValues.length > 1 && (
                <button
                  type='button'
                  onClick={() => handleRemoveValue(index)}
                  className='text-red-500 hover:text-red-700'
                  disabled={disabled}
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              )}
            </div>

            {isExpanded && (
              <div className='ml-6 space-y-1'>
                <div className='text-xs font-medium text-gray-600'>
                  Synonyms
                </div>
                {enumValue.synonyms.map((synonym, synonymIndex) => (
                  <div
                    key={`${stableKeysRef.current[index]}-synonym-${synonymIndex}`}
                    className='flex gap-2 items-center'
                  >
                    <CustomInput
                      value={synonym}
                      onChange={(e) =>
                        handleSynonymChange(index, synonymIndex, e.target.value)
                      }
                      placeholder={`Synonym ${synonymIndex + 1}`}
                      className='bg-white flex-1'
                      containerClassName='mb-0'
                      disabled={disabled}
                    />
                    <button
                      type='button'
                      onClick={() => handleRemoveSynonym(index, synonymIndex)}
                      className='text-red-500 hover:text-red-700'
                      disabled={disabled}
                    >
                      <Trash2 className='h-4 w-4' />
                    </button>
                  </div>
                ))}
                <button
                  type='button'
                  onClick={() => handleAddSynonym(index)}
                  className='py-1 flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs'
                  disabled={disabled}
                >
                  <Plus className='h-3 w-3' />
                  Add synonym
                </button>
              </div>
            )}
          </div>
        );
      })}
      <button
        type='button'
        onClick={handleAddValue}
        className='py-2 pb-4 flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm'
        disabled={disabled}
      >
        <Plus className='h-4 w-4' />
        Add value
      </button>
    </div>
  );
}

export default EnumeratedValuesManager;
